import {
	PinoutValidationResultEntryStatus,
	RequirementInstanceResultStatus,
	Utils,
} from '@cadstrom/sigma-engine-shared';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useState } from 'react';

import { AnalysisResultsModuleChip } from '../components/analysis-results-module-chip.js';
import { StatusBadge } from '../components/status-badge.js';

import type { PinoutValidationInstanceMetadata, PinoutValidationResultMetadata } from '@cadstrom/sigma-engine-shared';
import type { DataGridPremiumProps, GridColDef } from '@mui/x-data-grid-premium';

export type PinoutDetailTableProps = {
	instanceMetadata: PinoutValidationInstanceMetadata;
	resultMetadata: PinoutValidationResultMetadata;
	loading?: boolean;
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
};

const WARNING_STATUSES = [
	PinoutValidationResultEntryStatus.UNMAPPED,
	PinoutValidationResultEntryStatus.TWO_PIN,
	PinoutValidationResultEntryStatus.ERROR,
] as const;

interface PinoutDetailRow {
	id: number;
	status: PinoutValidationResultEntryStatus;
	designPin: string;
	designSignal: string;
	packageStorePin: string;
	packageStoreSignal: string;
	pinMismatch: boolean;
	packageStorePinSort: string;
}

interface PinoutDetailPanelToolbarProps {
	moduleReferenceDesignator: string;
	visibleStatuses: Set<PinoutValidationResultEntryStatus>;
	onStatusClick: (status: PinoutValidationResultEntryStatus, event: React.MouseEvent<HTMLButtonElement>) => void;
	summary: { total: number; success: number; error: number; warning: number; humanNeeded: number };
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
}

declare module '@mui/x-data-grid-premium' {
	interface ToolbarPropsOverrides extends PinoutDetailPanelToolbarProps {}
}

function PinoutDetailPanelToolbar({
	moduleReferenceDesignator,
	visibleStatuses,
	onStatusClick,
	summary,
	handleAddModuleFilter,
}: PinoutDetailPanelToolbarProps) {
	const hasAnyWarning = WARNING_STATUSES.some((status) => visibleStatuses.has(status));

	return (
		<div className='flex items-center justify-between border-b border-gray-a4 px-2 py-1'>
			<div className='flex items-center gap-2'>
				<AnalysisResultsModuleChip
					moduleReferenceDesignator={moduleReferenceDesignator}
					handleAddModuleFilter={handleAddModuleFilter}
				/>
				<div className='flex flex-wrap gap-1'>
					<Button
						onClick={(event) => onStatusClick(PinoutValidationResultEntryStatus.PASS, event)}
						color='success'
						size='small'
						variant={visibleStatuses.has(PinoutValidationResultEntryStatus.PASS) ? 'contained' : 'outlined'}
					>
						<span>Success: {summary.success}</span>
					</Button>
					<Button
						onClick={(event) => onStatusClick(PinoutValidationResultEntryStatus.UNMAPPED, event)}
						color='warning'
						size='small'
						variant={hasAnyWarning ? 'contained' : 'outlined'}
					>
						<span>Warning: {summary.warning}</span>
					</Button>
					<Button
						onClick={(event) => onStatusClick(PinoutValidationResultEntryStatus.FAIL, event)}
						color='error'
						size='small'
						variant={visibleStatuses.has(PinoutValidationResultEntryStatus.FAIL) ? 'contained' : 'outlined'}
					>
						<span>Error: {summary.error}</span>
					</Button>
					<Button
						onClick={(event) => onStatusClick(PinoutValidationResultEntryStatus.NOT_UNDERSTOOD, event)}
						color='orange'
						size='small'
						variant={visibleStatuses.has(PinoutValidationResultEntryStatus.NOT_UNDERSTOOD) ? 'contained' : 'outlined'}
					>
						<span>Human needed: {summary.humanNeeded}</span>
					</Button>
				</div>
			</div>
		</div>
	);
}

/**
 * Extracted body of `PinoutDetailPanel`. Consumes validated metadata directly
 * so both the V1 detail-panel shim and V2 activity-timeline payload renderer
 * produce identical output.
 */
export function PinoutDetailTable({
	instanceMetadata,
	resultMetadata,
	loading = false,
	handleAddModuleFilter,
}: PinoutDetailTableProps) {
	const results = useMemo(() => resultMetadata.results, [resultMetadata]);

	const [visibleStatuses, setVisibleStatuses] = useState<Set<PinoutValidationResultEntryStatus>>(
		new Set([
			PinoutValidationResultEntryStatus.FAIL,
			PinoutValidationResultEntryStatus.UNMAPPED,
			PinoutValidationResultEntryStatus.TWO_PIN,
			PinoutValidationResultEntryStatus.ERROR,
			PinoutValidationResultEntryStatus.NOT_UNDERSTOOD,
		]),
	);

	const summary = useMemo(() => {
		const stats = {
			total: results.length,
			success: 0,
			error: 0,
			warning: 0,
			humanNeeded: 0,
		};

		for (const result of results) {
			switch (result.status) {
				case PinoutValidationResultEntryStatus.PASS: {
					stats.success++;
					break;
				}
				case PinoutValidationResultEntryStatus.FAIL: {
					stats.error++;
					break;
				}
				case PinoutValidationResultEntryStatus.UNMAPPED:
				case PinoutValidationResultEntryStatus.TWO_PIN:
				case PinoutValidationResultEntryStatus.ERROR: {
					stats.warning++;
					break;
				}
				case PinoutValidationResultEntryStatus.NOT_UNDERSTOOD: {
					stats.humanNeeded++;
					break;
				}
			}
		}

		return stats;
	}, [results]);

	const handleStatusClick = useCallback(
		(status: PinoutValidationResultEntryStatus, event: React.MouseEvent<HTMLButtonElement>) => {
			const isWarningStatus = (WARNING_STATUSES as readonly PinoutValidationResultEntryStatus[]).includes(status);
			const statusesToToggle = isWarningStatus ? WARNING_STATUSES : [status];

			setVisibleStatuses((prev) => {
				if (event.metaKey || event.ctrlKey) {
					const newSet = new Set(prev);
					const hasAny = statusesToToggle.some((s) => newSet.has(s));

					if (hasAny) {
						for (const s of statusesToToggle) {
							newSet.delete(s);
						}
					} else {
						for (const s of statusesToToggle) {
							newSet.add(s);
						}
					}
					return newSet;
				}

				return new Set(statusesToToggle);
			});
		},
		[],
	);

	const detailRows = useMemo(() => {
		return results
			.map((result, index) => ({
				id: index,
				status: result.status,
				designPin: result.designPin ?? 'N/A',
				designSignal: result.designSignal ?? 'N/A',
				packageStorePin: result.packageStorePin ?? 'N/A',
				packageStoreSignal: result.packageStoreSignal ?? 'N/A',
				pinMismatch:
					result.designPin !== result.packageStorePin &&
					result.designPin !== undefined &&
					result.packageStorePin !== undefined,
				packageStorePinSort: result.packageStorePin ?? '',
			}))
			.sort((a, b) => {
				const pinA = a.packageStorePinSort === 'N/A' ? '' : a.packageStorePinSort;
				const pinB = b.packageStorePinSort === 'N/A' ? '' : b.packageStorePinSort;
				return Utils.naturalStringSort(pinA, pinB);
			})
			.filter((row) => visibleStatuses.has(row.status));
	}, [results, visibleStatuses]);

	const slots: DataGridPremiumProps['slots'] = { toolbar: PinoutDetailPanelToolbar };
	const slotProps: DataGridPremiumProps['slotProps'] = useMemo(
		() => ({
			toolbar: {
				moduleReferenceDesignator: instanceMetadata.referenceDesignator,
				visibleStatuses,
				onStatusClick: handleStatusClick,
				summary,
				handleAddModuleFilter,
			} satisfies PinoutDetailPanelToolbarProps,
		}),
		[instanceMetadata.referenceDesignator, visibleStatuses, handleStatusClick, summary, handleAddModuleFilter],
	);

	const detailPanelColumns: GridColDef<PinoutDetailRow>[] = [
		{
			field: 'status',
			headerName: 'Status',
			width: 100,
			renderCell: ({ value }) => {
				const status =
					value === PinoutValidationResultEntryStatus.PASS ? RequirementInstanceResultStatus.Success
					: value === PinoutValidationResultEntryStatus.FAIL ? RequirementInstanceResultStatus.Error
					: value === PinoutValidationResultEntryStatus.UNMAPPED ? RequirementInstanceResultStatus.Warning
					: value === PinoutValidationResultEntryStatus.TWO_PIN ? RequirementInstanceResultStatus.Warning
					: value === PinoutValidationResultEntryStatus.ERROR ? RequirementInstanceResultStatus.Warning
					: value === PinoutValidationResultEntryStatus.NOT_UNDERSTOOD ? RequirementInstanceResultStatus.HumanNeeded
					: RequirementInstanceResultStatus.Exception;

				return (
					<div className='flex h-full items-center gap-1'>
						<StatusBadge status={status} />
					</div>
				);
			},
		} satisfies GridColDef<PinoutDetailRow>,
		{
			field: 'designPin',
			headerName: 'Design Pin',
			width: 120,
			renderCell: ({ row: cellRow }) => (
				<div className='flex h-full items-center'>
					<Typography bgcolor='inherit'>{cellRow.designPin}</Typography>
				</div>
			),
		} satisfies GridColDef<PinoutDetailRow>,
		{
			field: 'designSignal',
			headerName: 'Design Signal',
			flex: 1,
		},
		{
			field: 'packageStorePin',
			headerName: 'Datasheet Pin',
			flex: 1,
		},
		{
			field: 'packageStoreSignal',
			headerName: 'Datasheet Signal',
			flex: 1,
		},
	];

	return (
		<div className='w-full overflow-auto bg-gray-a5'>
			<DataGridPremium
				hideFooter
				loading={loading && detailRows.length === 0}
				disableColumnMenu
				rows={detailRows}
				columns={detailPanelColumns}
				disableRowSelectionOnClick
				scrollEndThreshold={24}
				paginationMode='server'
				disableColumnSorting
				sortingMode='server'
				showToolbar
				slots={slots}
				slotProps={slotProps}
			/>
		</div>
	);
}
